import styled from "@emotion/styled"
import { Button, FormControlLabel, Modal, Radio, RadioGroup } from "@mui/material"
import { Attribute, Station } from "api"
import { LocationContext } from "contexts/LocationProvider"
import { useContext, useState } from "react"
import { MapOrList, trackMapOrListDisplay } from "reusable/ChargeGTM"
import {
  Desktop,
  FilterIcon,
  Mobile,
  PrinterIcon,
  ShowListIcon,
  ShowMapIcon,
  useIsDesktop
} from "ui"
import { SearchInput } from "./SearchInput"
import SideActions from "./SideActions"

const StyledContainer = styled.div`
  ${(props) => props.theme.breakpoints.down("md")} {
    display: none;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledPrintExport = styled.div`
  display: flex;
  ${(props) => props.theme.breakpoints.down("md")} {
    margin-left: auto;
  }
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 24px;
  }
`
const StyledMobileContainer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 20px;
  width: 100%;
`

const StyledMobileButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSearch = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 420px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 640px;
  }
`

const StyledSwitchLabel = styled.div<{ $active: boolean }>`
  background: ${(props) => (props.$active ? "#0050C9" : "none")};
  color: ${(props) => (props.$active ? "#FFF" : "#000")};
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 90px;
  height: 32px;
  display: flex;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 82px;
    font-size: 12px;
    line-height: 18px;
    height: 24px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 32px;
    width: 106px;
    font-size: 16px;
    line-height: 24px;
  }
`

const StyledModalCont = styled.div`
  background-color: white;
  height: 80%;
  padding: 20px;
  border-radius: 20px 20px 0 0;
  position: absolute;
  bottom: 0;
  overflow: auto;
  z-index: 10000;
`

const MobileFiltersButton = styled(Button)`
  border: 1px solid #5c6474;
  padding: 4px 20px 4px 20px;
  gap: 11px;
  display: flex;
  border-radius: 24px;
  color: #5c6474;
  font-size: 16px;
  line-height: 24px;
`

const StyledFormControlLabel = styled(FormControlLabel)`
margin:0;
border: 1px solid transparent;
& .MuiTypography-root{
  display:none;
}
& .MuiButtonBase-root{
  padding:0;
}
  :focus-visible {
  border: 1px solid red;
  /* border-radius: 36px; */
}
`

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  color: #0050c9;
  font-size: 14px;
  line-height: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 12px;
    line-height: 28px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 14px;
    line-height: 20px;
  }
  :focus-visible{
    border:1px solid gray;
  }
  gap: 4px;
`

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction:row;
  border: 1px solid rgba(21, 62, 105, 0.3);
  padding: 8px;
  border-radius: 28px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 6px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding: 8px;
  }
`

function RadioIcon({ text, label, checked }: { text: string; label: 'map' | 'list'; checked: boolean }) {
  return <StyledSwitchLabel $active={checked}>
    {label === 'list' && <ShowListIcon fill={checked ? "#FFF" : "#000"} />}
    {label === 'map' && <ShowMapIcon fill={checked ? "#FFF" : "#000"} />}
    {text}
  </StyledSwitchLabel>
}

type Props = {
  metadata: Attribute[]
  userLoc: {
    lat: number
    lon: number
  } | null
  locations: Station[]
  onPrint: () => void,
  onDownload: () => void,
  focusOnSpecificLocation: (lat: number, lon: number) => void
  setShowCarousel: (value: boolean) => void
}

type ActionProps = {
  onPrint: () => void,
  onDownload: () => void,

}

export function Actions({ onPrint, onDownload }: ActionProps) {
  return (
    <StyledPrintExport>
      <StyledButton onClick={onDownload} disableRipple>
        <PrinterIcon />
        הורדת קובץ
      </StyledButton>
      <StyledButton onClick={() => onPrint()} disableRipple>
        <PrinterIcon />
        הדפסה
      </StyledButton>
    </StyledPrintExport>
  )
}

function TopActions({ locations, metadata, userLoc, onPrint, onDownload, focusOnSpecificLocation, setShowCarousel }: Props) {
  const isDesktop = useIsDesktop()
  const { showAsList, setShowAsList } = useContext(LocationContext)
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
  }
  const handleOpen = () => {
    setOpen(true)
  }
  const handleShowListMobile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'map') {
      setShowAsList(false)
      setShowCarousel(true)
    } else {
      setShowAsList(true)
      setShowCarousel(false)
    }
  }
  const handleChangeNew = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowAsList(event.target.value === 'map' ? false : true)
    trackMapOrListDisplay(MapOrList.LIST)
  };
  return (
    <>
      <StyledContainer>
        <StyledRadioGroup
          aria-label="לתצוגה נגישה, בחר ברשימה"
          value={showAsList ? 'list' : 'map'}
          name="radio-group-desktop"
          onChange={handleChangeNew}
        >
          <StyledFormControlLabel
            value={'map'}
            control={
              <Radio
                disableRipple
                checkedIcon={<RadioIcon text={'מפה'} checked={!showAsList} label="map" />}
                icon={<RadioIcon text={'מפה'} checked={!showAsList} label="map" />}
              />
            }
            label={'מפה'}
          />
          <StyledFormControlLabel
            value={'list'}
            control={
              <Radio
                disableRipple
                checkedIcon={<RadioIcon text={'רשימה'} checked={showAsList} label="list" />}
                icon={<RadioIcon text={'רשימה'} checked={showAsList} label="list" />}
              />
            }
            label={'רשימה'}
          />
        </StyledRadioGroup>
        <StyledSearch>
          <SearchInput
            placeholder={"הזן מיקום"}
            searchIcon={isDesktop ? false : true}
            radius="28px"
            focusOnSpecificLocation={focusOnSpecificLocation}
          />
        </StyledSearch>
        <Desktop>
          <Actions onPrint={onPrint} onDownload={() => onDownload()} />
        </Desktop>
      </StyledContainer>

      <StyledMobileContainer>
        <StyledSearch>
          <SearchInput
            placeholder={"הזן מיקום"}
            searchIcon={isDesktop ? false : true}
            radius="28px"
            focusOnSpecificLocation={focusOnSpecificLocation}
          />
        </StyledSearch>
        <StyledMobileButtons>
          <MobileFiltersButton onClick={handleOpen}>
            <FilterIcon />
            סינון
          </MobileFiltersButton>
          <StyledRadioGroup
            aria-label="שינוי תצוגת העמדות"
            value={showAsList ? 'list' : 'map'}
            name="radio-group-mobile"
            onChange={handleShowListMobile}
            id="radio-group-mobile"
          >
            <StyledFormControlLabel
              value={'map'}
              control={
                <Radio
                  disableRipple
                  checkedIcon={<RadioIcon text={'מפה'} checked={!showAsList} label="map" />}
                  icon={<RadioIcon text={'מפה'} checked={!showAsList} label="map" />}
                />
              }
              label={'מפה'}
            />
            <StyledFormControlLabel
              value={'list'}
              control={
                <Radio
                  disableRipple
                  checkedIcon={<RadioIcon text={'רשימה'} checked={showAsList} label="list" />}
                  icon={<RadioIcon text={'רשימה'} checked={showAsList} label="list" />}
                />
              }
              label={'רשימה'}
              aria-label="רשימה נגישה"
            />
          </StyledRadioGroup>
        </StyledMobileButtons>
      </StyledMobileContainer>
      <Mobile>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <StyledModalCont>
            <SideActions
              metadata={metadata}
              userLoc={userLoc}
              locations={locations}
              handleClose={handleClose}
            />
          </StyledModalCont >
        </Modal>
      </Mobile>
    </>
  )
}

export default TopActions
